import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {FormBuilder, Validators} from '@angular/forms';
import {Bank, PublicService} from 'web-angular-client';

import {AlertService} from '../../service/alert.service';
import {AuthenticationService} from '../../service/authentication.service';
import {EnvironmentService} from '../../service/environment.service';
import {BankDetails} from '../../interface/model/banking-details';
import {WizardStepForm} from '../../interface/form/wizard-step-form';

/**
 * The banking/AVS details form shared by the signup wizard and the eCommerce KYC verification
 * wizard. Emits the captured banking details to the hosting wizard step.
 */
@Component({
    selector: 'app-avs-form',
    templateUrl: './avs-form.component.html',
    standalone: false
})
export class AvsFormComponent extends WizardStepForm<BankDetails> implements OnInit, OnChanges
{
    @Input()
    prefilledBankDetail: BankDetails;

    /**
     * Re-applies the pre-fill and overwrites the branch with the selected bank's universal branch
     * number once the banks load — the signup wizard's pre-extraction behaviour; off for KYC so a
     * submitted branch is preserved.
     */
    @Input()
    refreshBranchAfterBankLoad = false;

    @Output()
    previousPage: EventEmitter<boolean>;

    @Output()
    bankingDetail: EventEmitter<BankDetails>;

    @Output()
    nextPage: EventEmitter<number>;

    banks: Bank[];

    constructor(
        protected router: Router,
        protected formBuilder: FormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        protected publicService: PublicService)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
        this.previousPage = new EventEmitter();
        this.nextPage = new EventEmitter<number>();
        this.bankingDetail = new EventEmitter();
    }

    get formControls()
    {
        return this.form.controls;
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        this.patchPrefilledDataToForm(this.prefilledBankDetail);
    }

    ngOnInit(): void
    {
        this.form = this.formBuilder.group({
            bankName: [
                '',
                Validators.required
            ],
            bankBranch: [
                '',
                [Validators.required, Validators.maxLength(10), Validators.pattern('^[0-9]*$')],
            ],
            bankAccountNumber: [
                '',
                [Validators.required, Validators.maxLength(30), Validators.pattern('^[0-9]*$')],
            ]
        });

        this.applyReadOnlyState();
        this.patchPrefilledDataToForm(this.prefilledBankDetail);

        this.publicService.getBanks().subscribe({
            next: (banks: Bank[]) =>
            {
                this.banks = banks;

                if (this.refreshBranchAfterBankLoad)
                {
                    this.patchPrefilledDataToForm(this.prefilledBankDetail);
                    this.updateBank();
                }
            },
            error: () =>
            {
                this.alertService.error(
                    'We are having trouble retrieving the list of available banks right now. Please try again later.');
            }
        });
    }

    /**
     * Emits the captured banking details and moves the wizard to the next step. In read-only "View
     * submission" mode the details are not emitted, so the disabled form's values cannot overwrite
     * the hosting wizard's state.
     */
    submit(): void
    {
        if (!this.readOnly)
        {
            this.bankingDetail.emit({
                bankName: this.formControls.bankName.value, bankBranch: this.formControls.bankBranch.value,
                bankAccountNumber: this.formControls.bankAccountNumber.value
            } as BankDetails);
        }
        this.nextPage.emit();
    }

    /**
     * Lets the hosting wizard know the user wants to go back to the previous step.
     */
    emitGoToPrevious(): void
    {
        this.previousPage.emit(true);
    }

    /**
     * Auto-populates the branch with the selected bank's universal branch number.
     */
    updateBank(): void
    {
        const bankName: string = this.formControls.bankName.value;
        const bank: Bank = this.banks.find(availableBank => availableBank.bankName === bankName);

        this.formControls.bankBranch.setValue(bank?.defaultBranchNumber ?? null);
    }
}
